<script setup lang="ts">
/**
 * Body of /resume-examples/<slug>.
 *
 * Order follows doc/seo/brief-resume-examples.md: the answer first, then the
 * finished resume (rendered and as text), the summary at each level, the
 * skills, the bullets before and after, what the role needs on top of
 * experience, and the template. The page adds the FAQ after it, and then the
 * related examples with the one offer (MarketingResumeExampleNext).
 *
 * The sheet is printed by the server from its own copy of the sample
 * (`sample` = the slug), clean like /demo. The download is the free PDF, so
 * it carries the watermark on every layout but Basic.
 */
import { PDF_TEMPLATES, type PdfTemplateId } from '~/constants/cvTemplates'
import { exampleContentLang, roleName, rolePhrase, rolePhraseWithArticle, type ResumeExampleEntry, type ResumeExampleIndexItem } from '~/constants/resumeExamples'

const props = defineProps<{
  entry: ResumeExampleEntry
  item: ResumeExampleIndexItem
}>()

const { t, locale } = useI18n()
const { startCVForm } = useStartCVForm()

const role = computed(() => roleName(props.item, locale.value))
const phrase = computed(() => rolePhrase(props.item, locale.value))
const phraseWithArticle = computed(() => rolePhraseWithArticle(props.item, locale.value))
/** The Czech page shows, prints and downloads the Czech translation of the sample. */
// The translation this page shows ('cs', 'de'), or null for the English original.
const contentLang = computed(() => exampleContentLang(props.item, locale.value))
const sampleId = computed(() => (contentLang.value ? `${contentLang.value}/${props.item.slug}` : props.item.slug))

const selectedTemplate = ref<PdfTemplateId>(props.item.template)
const downloadUrl = computed(() => `/api/resume-examples/${props.item.slug}/${selectedTemplate.value}${contentLang.value ? `.${contentLang.value}` : ''}.pdf`)

const levelsTitle = computed(() =>
  props.item.kind === 'profession'
    ? t('resumeExamples.page.levels.titleProfession', { role: role.value })
    : t('resumeExamples.page.levels.titleSituation', { role: role.value }),
)

const jumpLinks = computed(() => [
  { id: 'example', label: t('resumeExamples.page.jump.example') },
  { id: 'summaries', label: t('resumeExamples.page.jump.summaries') },
  { id: 'skills', label: t('resumeExamples.page.jump.skills') },
  { id: 'bullets', label: t('resumeExamples.page.jump.bullets') },
  { id: 'extras', label: props.entry.extras.title },
  { id: 'template', label: t('resumeExamples.page.jump.template') },
  { id: 'faq', label: t('resumeExamples.page.jump.faq') },
])

function useExample() {
  startCVForm({ example: props.item.slug, template: selectedTemplate.value })
}

/** A blank CV on this role's template: the phrases are offered as the person writes. */
function startBlank() {
  startCVForm({ template: selectedTemplate.value })
}
</script>

<template>
  <div class="sofax">
    <!-- The answer: what this resume has to show at the top -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('resumeExamples.page.shortLabel') }}
            </div>
            <p class="text-lg">{{ entry.short }}</p>
          </div>

          <nav class="mx-auto mt-8 max-w-3xl" :aria-label="t('resumeExamples.page.jumpLabel')">
            <ul class="flex flex-wrap gap-3">
              <li v-for="link in jumpLinks" :key="link.id">
                <a :href="`#${link.id}`" class="inline-block rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-surface dark:border-gray-700 dark:text-white dark:hover:bg-gray-800">
                  {{ link.label }}
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </section>

    <!-- The finished resume: the sheet, what to take from it, and the same as text -->
    <section id="example" class="surface-alt scroll-mt-24">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeExamples.page.sample.eyebrow')"
            :title="t('resumeExamples.page.sample.title', { role: phrase })"
            :text="t('resumeExamples.page.sample.lead')"
            width="max-w-4xl"
          />

          <div class="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.2fr_.8fr]">
            <div class="min-w-0">
              <div
                role="radiogroup"
                :aria-label="t('resumeExamples.page.sample.templateLabel')"
                class="template-switch mb-4 flex w-full overflow-x-auto rounded-full border border-line bg-white p-1 dark:border-gray-700 dark:bg-gray-800"
              >
                <button
                  v-for="id in PDF_TEMPLATES"
                  :key="id"
                  type="button"
                  role="radio"
                  :aria-checked="selectedTemplate === id"
                  class="flex-1 shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors duration-300 sm:px-4"
                  :class="selectedTemplate === id
                    ? 'bg-ink text-white dark:bg-white dark:text-ink'
                    : 'text-body hover:text-ink dark:text-gray-300 dark:hover:text-white'"
                  @click="selectedTemplate = id"
                >
                  {{ t(`cv.templates.${id}.name`) }}
                </button>
              </div>

              <div class="overflow-hidden rounded-[0.625rem] bg-gray-100 dark:bg-gray-900">
                <!-- `sample` is the slug: the server prints its own copy of this
                     example, clean, whatever the page sends as cv-data. -->
                <LazyCVPreviewWrapper
                  :cv-data="entry.cv"
                  :experiences-list="entry.cv.experiences"
                  :education-list="entry.cv.education"
                  :show-q-r-code="false"
                  :template="selectedTemplate"
                  :sample="sampleId"
                  frame="border"
                />
              </div>
            </div>

            <div class="flex flex-col gap-6">
              <div class="panel !p-6">
                <SofaxBtn variant="accent" block :label="t('resumeExamples.page.sample.use')" @click="useExample" />
                <p class="mt-3 text-sm text-muted dark:text-gray-400">{{ t('resumeExamples.page.sample.useNote') }}</p>

                <a :href="downloadUrl" download rel="nofollow" class="btn btn-outline mt-5 w-full">
                  <span>{{ t('resumeExamples.page.sample.download') }}</span>
                  <span aria-hidden="true">{{ t('resumeExamples.page.sample.download') }}</span>
                </a>
                <p class="mt-3 text-sm text-muted dark:text-gray-400">{{ t('resumeExamples.page.sample.downloadNote') }}</p>
              </div>

              <div>
                <h3 class="!text-2xl">{{ t('resumeExamples.page.sample.notesTitle') }}</h3>
                <ul class="mt-4 flex flex-col gap-4">
                  <li v-for="(note, index) in entry.sampleNotes" :key="index" class="flex items-start gap-3 text-base">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-lg text-accent" aria-hidden="true" />
                    <span>{{ note }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="mx-auto mt-14 max-w-3xl">
            <h3 class="!text-2xl">{{ t('resumeExamples.page.sample.textTitle', { role: phrase }) }}</h3>
            <p class="mb-6 mt-3 text-base">{{ t('resumeExamples.page.sample.textLead') }}</p>
            <MarketingResumeExampleText :cv="entry.cv" />
            <p class="mt-4 text-sm text-muted dark:text-gray-400">{{ t('resumeExamples.page.sample.caption') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- The summary at each level (or in each situation) -->
    <section id="summaries" class="scroll-mt-24 bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeExamples.page.levels.eyebrow')"
            :title="levelsTitle"
            :text="t(`resumeExamples.page.levels.lead.${item.kind}`)"
            width="max-w-4xl"
          />
          <ul class="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-3">
            <li v-for="level in entry.levels" :key="level.id" class="flex flex-col rounded-[0.625rem] bg-surface p-6 dark:bg-gray-800">
              <h3 class="!text-xl">{{ level.label }}</h3>
              <p class="mt-4 rounded-[0.625rem] bg-white p-4 text-base dark:bg-gray-900">{{ level.summary }}</p>
              <p class="mt-4 text-sm text-muted dark:text-gray-400">{{ level.note }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Skills, grouped the way postings group them -->
    <section id="skills" class="surface-alt scroll-mt-24">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeExamples.page.skills.eyebrow')"
            :title="t('resumeExamples.page.skills.title', { role: phraseWithArticle })"
            :text="entry.skills.note"
            width="max-w-4xl"
          />
          <div class="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-[2fr_1fr]">
            <div class="rounded-[0.625rem] bg-white p-6 dark:bg-gray-800">
              <h3 class="!text-2xl">{{ t('resumeExamples.page.skills.hardTitle') }}</h3>
              <div v-for="group in entry.skills.hard" :key="group.group" class="mt-6">
                <h4 class="!text-base font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ group.group }}</h4>
                <ul class="mt-3 flex flex-wrap gap-2">
                  <li v-for="skill in group.items" :key="skill" class="rounded-full bg-surface px-3 py-1.5 text-sm font-semibold text-ink dark:bg-gray-900 dark:text-white">
                    {{ skill }}
                  </li>
                </ul>
              </div>
            </div>
            <div class="rounded-[0.625rem] bg-white p-6 dark:bg-gray-800">
              <h3 class="!text-2xl">{{ t('resumeExamples.page.skills.softTitle') }}</h3>
              <ul class="mt-6 flex flex-wrap gap-2">
                <li v-for="skill in entry.skills.soft" :key="skill" class="rounded-full bg-surface px-3 py-1.5 text-sm font-semibold text-ink dark:bg-gray-900 dark:text-white">
                  {{ skill }}
                </li>
              </ul>
              <p class="mt-6 text-sm text-muted dark:text-gray-400">{{ t('resumeExamples.page.skills.softNote') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Bullets, before and after -->
    <section id="bullets" class="scroll-mt-24 bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeExamples.page.bullets.eyebrow')"
            :title="t('resumeExamples.page.bullets.title', { role: role })"
            :text="t('resumeExamples.page.bullets.lead')"
            width="max-w-4xl"
          />
          <ul class="mx-auto flex max-w-3xl flex-col gap-4">
            <li v-for="(bullet, index) in entry.bullets" :key="index" class="rounded-[0.625rem] bg-surface p-5 dark:bg-gray-900">
              <!-- line-through on the text span only, or the icon is struck too -->
              <p class="!mb-3 flex items-start gap-3 text-base text-muted dark:text-gray-400">
                <i class="bi bi-x-circle mt-0.5 shrink-0 text-lg" :aria-label="t('templatesPage.intro.before')" />
                <span class="line-through">{{ bullet.before }}</span>
              </p>
              <p class="flex items-start gap-3 text-base">
                <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-lg text-accent" :aria-label="t('templatesPage.intro.after')" />
                <span>{{ bullet.after }}</span>
              </p>
            </li>
          </ul>
          <p class="mx-auto mt-6 max-w-3xl text-center">
            <LocaleLink to="/resume-synonyms" class="link">{{ t('resumeExamples.page.bullets.synonymsLink') }} →</LocaleLink>
          </p>

          <!-- The same bullets are offered in the editor (components/CV/Editor/PhraseSuggestions.vue) -->
          <div class="mx-auto mt-10 flex max-w-3xl flex-col items-start gap-5 rounded-[0.625rem] border-2 border-accent/30 p-6 sm:flex-row sm:items-center">
            <SofaxIconTile icon="bi-lightbulb" size="sm" />
            <div class="flex-1">
              <div class="display-heading display-heading-5 mb-1">{{ t('resumeExamples.page.bullets.editorTitle') }}</div>
              <p class="text-base">{{ t('resumeExamples.page.bullets.editorText', { role: phrase }) }}</p>
            </div>
            <SofaxBtn variant="dark" class="shrink-0" :label="t('resumeExamples.page.bullets.editorCta')" @click="startBlank" />
          </div>
        </div>
      </div>
    </section>

    <!-- What the role needs on top of experience -->
    <section id="extras" class="surface-alt scroll-mt-24">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeExamples.page.extras.eyebrow')"
            :title="entry.extras.title"
            :text="entry.extras.intro"
            width="max-w-4xl"
          />
          <ul class="mx-auto grid max-w-5xl grid-cols-1 gap-4 md:grid-cols-2">
            <li v-for="extra in entry.extras.items" :key="extra.name" class="rounded-[0.625rem] bg-white p-6 dark:bg-gray-800">
              <h3 class="!text-xl">{{ extra.name }}</h3>
              <p class="mt-2 text-base">{{ extra.text }}</p>
              <p v-if="extra.issuer" class="mt-3 text-sm text-muted dark:text-gray-400">
                {{ t('resumeExamples.page.extras.issuer') }}
                <a v-if="extra.url" :href="extra.url" class="link" target="_blank" rel="noopener">{{ extra.issuer }}</a>
                <template v-else>{{ extra.issuer }}</template>
              </p>
            </li>
          </ul>
          <p v-if="item.kind === 'profession'" class="mx-auto mt-8 max-w-3xl text-center">
            <LocaleLink to="/blog/how-to-list-certifications-on-a-resume" class="link">{{ t('resumeExamples.page.extras.certLink') }} →</LocaleLink>
          </p>
          <ul v-if="entry.links?.length" class="mx-auto mt-6 flex max-w-3xl flex-col items-center gap-3">
            <li v-for="link in entry.links" :key="link.to">
              <LocaleLink :to="link.to" class="link">{{ link.label }} →</LocaleLink>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Which template, and why -->
    <section id="template" class="scroll-mt-24 bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl">
            <div class="eyebrow mb-5">{{ t('resumeExamples.page.template.eyebrow') }}</div>
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('resumeExamples.page.template.title', { role: phraseWithArticle }) }}</h2>
            <p class="mt-5 text-lg">
              <strong class="text-ink dark:text-white">{{ t(`cv.templates.${item.template}.name`) }}.</strong>
              {{ entry.templateNote }}
            </p>
            <p class="mt-6 flex flex-col gap-3 sm:flex-row sm:gap-8">
              <LocaleLink to="/ats-friendly-resume-template" class="link">{{ t('resumeExamples.page.template.atsLink') }} →</LocaleLink>
              <LocaleLink to="/templates" class="link">{{ t('resumeExamples.page.template.templatesLink') }} →</LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* The layouts don't all fit a narrow phone: the row scrolls, without a bar. */
.template-switch {
  scrollbar-width: none;
}

.template-switch::-webkit-scrollbar {
  display: none;
}
</style>
